import { v2UsersLoginSocialAppleCreate } from '@on3/api';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { externalApi, useAuth } from '@on3/ui-lib/index';
import { webUrl } from '@on3/ui-lib/utils/api';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    AppleID: any;
  }
}

const loadApple = () =>
  new Promise((res, rej) => {
    const script = document.createElement('script');

    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;

    script.onload = () => res(true);
    script.onerror = () => rej(new Error('Apple API script failed to load'));

    document.body.appendChild(script);
  });

const signInUser = async (idToken: string) => {
  const user = await v2UsersLoginSocialAppleCreate(externalApi, {
    idToken,
    registerIfNew: true,
  });

  return user;
};

export const AppleButton = () => {
  const { setUserToken } = useAuth();

  const params = useSearchParams();
  const returnTo = params.get('returnTo') || '/';

  const router = useRouter();

  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    loadApple().then(() => {
      if (window.AppleID) {
        setScriptLoaded(true);

        window.AppleID.auth.init({
          clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID || '',
          scope: 'name email',
          redirectURI: `${webUrl}/login`,
          usePopup: true,
        });
      }
    });
  }, []);

  const handleAppleAuth = useCallback(async () => {
    if (scriptLoaded) {
      try {
        const res = await window.AppleID.auth.signIn();
        const user = await signInUser(res.authorization.id_token);

        window.top?.postMessage({
          type: 'USER_LOGIN',
          jwt: user,
        });

        setUserToken(user);
        router.push(returnTo);
      } catch (error) {
        console.error('Error signing in user:', error);
      }
    }
  }, [returnTo, router, scriptLoaded, setUserToken]);

  return (
    <button disabled={!scriptLoaded} onClick={handleAppleAuth} type="button">
      <SocialIcon type="apple" />
      <span className="sr-only">Sign in with Apple</span>
    </button>
  );
};
